import { useContext, useState } from 'react'

import { selectPlaidItems } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useHeardPlaid } from '@/components/plaid/helpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'
import type { PlaidItem } from '@/reducers/finances/plaidItemReducer'

import { Button, Dialog, List, Text } from '@/ui'

import { PlaidLinkContext } from './plaid/LinkContext'

const DuplicateInstitutionDialogActive = ({
  institution,
}: {
  institution: PlaidItem
}) => {
  const { setDuplicateInstitution } = useContext(PlaidLinkContext)
  const track = useAnalyticsTrack()
  const [showDialog, setShowDialog] = useState(true)

  const { open: openPlaid, ready: plaidReady } = useHeardPlaid({
    account: institution.accounts[0] ?? null,
    disconnected: false,
    redirectLink: '',
    shouldRedirectOnExit: false,
  })

  return (
    <Dialog open={showDialog}>
      <Dialog.Content>
        <Text variant={TypographyStyles.H2}> Connection already exists </Text>

        <Text>
          You have a connection to <b>{institution.institutionName}</b> with the
          following accounts:
        </Text>

        <List bulleted>
          {institution.accounts?.map((account) => {
            if (account.inactive === false) {
              return (
                <List.Item key={account.id}>
                  {account.name} {account.mask}
                </List.Item>
              )
            }

            return null
          })}
        </List>

        <Text>
          We&apos;ll direct you back to Plaid, where you&apos;ll be able to
          add/remove accounts via your existing connection.
        </Text>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button
              variant="secondary"
              onClick={() => setDuplicateInstitution('')}
            >
              Cancel
            </Button>
          </Dialog.Close>

          <Button
            disabled={!plaidReady}
            onClick={() => {
              track('clicked plaid add_remove', {
                fi_name: institution.institutionName ?? '',
              })
              setShowDialog(false)
              openPlaid()
            }}
          >
            Back to Plaid
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export const DuplicateInstitutionDialog = () => {
  const plaidInstitutions = useReselector(selectPlaidItems)
  const { duplicateInstitution } = useContext(PlaidLinkContext)

  if (!duplicateInstitution || !plaidInstitutions) return null

  const institution = Object.values(plaidInstitutions).find(
    (item) => item.institutionId === duplicateInstitution
  )

  if (!institution) return null

  return <DuplicateInstitutionDialogActive institution={institution} />
}
